import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import type { MgaSheetColumn } from './useData';
import { isLockedSheetColumn } from './useData';

const BEHAVIOR_LABEL: Record<string, string> = {
  MANUAL: 'Manual',
  CARRY_FORWARD: 'Carry forward',
  DERIVED: 'Derived',
  REQUEST_MIRROR: 'Request mirror',
};

const TYPE_LABEL: Record<string, string> = {
  EARNING: 'Earning',
  DEDUCTION: 'Deduction',
  INFO: 'Info',
};

export const moduleConfig = {
  module: {
    title: 'MGA sheet columns',
    permissionModuleKey: 'mga',
    route: {
      list: '/system/mga-sheet-columns',
      new: '/system/mga-sheet-columns/new',
      detail: (id: number) => `/system/mga-sheet-columns/${id}`,
      edit: (id: number) => `/system/mga-sheet-columns/${id}/edit`,
    },
    api: {
      list: '/mga/sheet-columns',
      detail: (id: number) => `/mga/sheet-columns/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: { placeholder: 'Search by code or name…' },
  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '', fields: ['columnType', 'behavior', 'isActive'] },
  ],
  filters: {
    fields: [
      {
        key: 'columnType',
        label: 'Type',
        placeholder: 'All types',
        getValue: (item: MgaSheetColumn) => item.columnType,
        options: [
          { value: 'EARNING', label: 'Earning' },
          { value: 'DEDUCTION', label: 'Deduction' },
        ],
      },
      {
        key: 'behavior',
        label: 'Behavior',
        placeholder: 'All behaviors',
        getValue: (item: MgaSheetColumn) => item.behavior,
        options: Object.entries(BEHAVIOR_LABEL).map(([value, label]) => ({ value, label })),
      },
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: MgaSheetColumn) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ],
      },
    ],
    applyFilter: (items: MgaSheetColumn[], filters: Record<string, string[]>) =>
      items.filter((item) => {
        if (filters.columnType?.length && !filters.columnType.includes(item.columnType)) return false;
        if (filters.behavior?.length && !filters.behavior.includes(item.behavior)) return false;
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      }),
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New sheet column',
        showTitle: false,
        variant: 'primary' as const,
        href: '/system/mga-sheet-columns/new',
      },
    ],
  },
  table: { actionsPosition: 'left' as const, keyExtractor: (item: MgaSheetColumn) => item.id },
  emptyStates: {
    noData: 'Configure settlement columns for the MGA payroll grid.',
    noSearchResults: 'No sheet columns match your search.',
    noFilterResults: 'No sheet columns match the filters.',
  },
  delete: {
    title: 'Remove sheet column',
    message: 'Remove this column from the MGA sheet configuration?',
    confirmText: 'Remove',
    cancelText: 'Cancel',
    errorMessage: 'Could not remove sheet column.',
    handler: async (id: number) => {
      const { deleteMgaSheetColumn } = await import('../../../services/mutations');
      await deleteMgaSheetColumn(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : ''),
  },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This sheet column does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Sheet column',
    getTitle: (item: MgaSheetColumn) => item.name || `Column #${item.id}`,
  },
  form: {
    new: {
      title: 'New MGA sheet column',
      subtitle: 'Adds a manual settlement field to the payroll grid',
      submitText: 'Create column',
    },
    edit: {
      title: 'Edit MGA sheet column',
      subtitle: 'Update label, order, and visibility on the payroll sheet',
      submitText: 'Save changes',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<MgaSheetColumn>) => {
      const { createMgaSheetColumn } = await import('../../../services/mutations');
      return createMgaSheetColumn(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<MgaSheetColumn>) => {
      const { updateMgaSheetColumn } = await import('../../../services/mutations');
      return updateMgaSheetColumn(id, data as Record<string, unknown>);
    },
  },
};

export const getDetailSections = (item: MgaSheetColumn, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Code', value: item.code },
      { label: 'Name', value: item.name },
      {
        label: 'Protected',
        value: isLockedSheetColumn(item)
          ? item.behavior !== 'MANUAL'
            ? 'Locked — system column used by payroll (cannot be deleted)'
            : 'Locked — in use on MGA periods (cannot be deleted)'
          : 'Editable — may be deleted if unused',
      },
      {
        label: 'In use',
        value: item.inUse ? 'Yes — has settlement amounts on MGA periods' : 'No',
      },
      { label: 'Behavior', value: BEHAVIOR_LABEL[item.behavior] ?? item.behavior },
      { label: 'Column type', value: TYPE_LABEL[item.columnType] ?? item.columnType },
      { label: 'Data type', value: item.dataType },
      { label: 'Include in total', value: item.includeInTotal ? 'Yes' : 'No' },
      { label: 'Show in grid', value: item.showInGrid ? 'Yes' : 'No' },
      { label: 'Sort order', value: String(item.sortOrder) },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Posting bucket', value: item.postingBucket || '—' },
      { label: 'Source key', value: item.sourceKey || '—' },
      { label: 'Notes', value: item.notes || '' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ],
  },
];

export const getStatusBadges = (item?: MgaSheetColumn) => {
  if (!item || !isLockedSheetColumn(item)) return null;
  return (
    <Badge variant="secondary" className="rounded-md px-2 py-0.5">
      Locked
    </Badge>
  );
};

const TextCell: React.FC<{ value?: string | null }> = ({ value }) => (
  <span className="text-sm whitespace-nowrap">{value || ''}</span>
);

export const getColumns = (formatDate: (s?: string) => string): Column<MgaSheetColumn>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'behavior',
    header: 'Behavior',
    sortable: true,
    render: (v) => <TextCell value={BEHAVIOR_LABEL[String(v)] ?? String(v ?? '')} />,
  },
  {
    key: 'columnType',
    header: 'Type',
    sortable: true,
    render: (v) => <TextCell value={TYPE_LABEL[String(v)] ?? String(v ?? '')} />,
  },
  {
    key: 'showInGrid',
    header: 'Grid',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.showInGrid ? 'success' : 'secondary'}>
        {v ?? row?.showInGrid ? 'Yes' : 'No'}
      </Badge>
    ),
  },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'}>
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    ),
  },
  { key: 'sortOrder', header: 'Order', sortable: true, render: (v) => <TextCell value={String(v ?? '')} /> },
  { key: 'updatedAt', header: 'Updated', sortable: true, render: (v) => formatDate(v) },
];

const TableActionsCell: React.FC<{
  item: MgaSheetColumn;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:bg-indigo-50 rounded-lg"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      View
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:bg-blue-50 rounded-lg ml-1"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        Edit
      </Link>
    )}
    {canDelete && !isLockedSheetColumn(item) && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:bg-red-50 rounded-lg ml-1"
        title="Remove"
      >
        Remove
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: MgaSheetColumn,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
) => (
  <TableActionsCell
    item={item}
    onDelete={onDelete}
    canDelete={options?.canDelete !== false}
    canEdit={options?.canEdit !== false}
  />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getInitialFormData = (): Partial<MgaSheetColumn> => ({
  code: '',
  name: '',
  columnType: 'DEDUCTION',
  dataType: 'DECIMAL',
  includeInTotal: true,
  sortOrder: 100,
  isActive: true,
  showInGrid: true,
  notes: '',
});

export const mapItemToFormData = (item: MgaSheetColumn): Partial<MgaSheetColumn> => ({
  code: item.code,
  name: item.name,
  columnType: item.columnType,
  dataType: item.dataType,
  includeInTotal: item.includeInTotal,
  sortOrder: item.sortOrder,
  isActive: item.isActive,
  showInGrid: item.showInGrid,
  postingBucket: item.postingBucket,
  notes: item.notes ?? '',
});

export type ModuleConfig = typeof moduleConfig;
